import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { TransactionsState, TransactionsStore } from './transactions.store';
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { iif, Observable, of } from 'rxjs';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter,
    QueryFilterGroupBy
} from '../../../shared/state/query-filter.functions';
import {
    eachMonthOfInterval,
    eachQuarterOfInterval,
    eachYearOfInterval,
    format,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
    subYears
} from 'date-fns';
import { Transaction } from './transaction.model';
import { get } from 'dot-prop';
import { setLastHourOfDayUTC } from 'src/app/shared/utils/date-utils';
import { setFirstHourOfDayUTC } from 'src/app/shared/utils/date-utils';
import { TransactionsService } from './transactions.service';

@Injectable({ providedIn: 'root' })
export class TransactionsQuery extends QueryEntity<TransactionsState> {
    constructor(
        protected store: TransactionsStore,
        private _service: TransactionsService
    ) {
        super(store);
    }

    all$ = this.selectAll();
    loading$ = this.select((state) => state.loading);

    selectTransactionByFilter$(
        filters: InferFilter<Transaction> = {},
        sortBy: keyof Transaction | any = 'transactionTimestamp',
        sortByOrder: Order = Order.DESC
    ): Observable<Transaction[]> {
        return this.selectAll({
            sortBy,
            sortByOrder,
            filterBy: (transaction) => {
                // check if transaction is filtered based on status...
                if (!filters?.transactionStatus) {
                    // ...if not exclude cancelled transactions by default
                    filters = {
                        ...filters,
                        transactionStatus: {
                            ne: 'CANCEL'
                        }
                    };
                }
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(transaction, key);
                        return !checkFilter<Transaction>(value, criteria, transaction);
                    });
                } else {
                    return true;
                }
            }
        });
    }

    selectTransactionById$(transactionId: number) {
        return this.selectEntity(transactionId);
    }

    selectTransactionsByInstrumentId$ = (
        instrumentId: Array<number> | number,
        includeLinkedTransactions: boolean = false
    ) =>
        this.selectAll({
            sortBy: 'importedAt',
            sortByOrder: Order.DESC,
            filterBy: (entity) => {
                // exclude cancelled transactions
                if (entity?.transactionStatus === 'CANCEL') {
                    return false;
                }
                if (typeof instrumentId === 'number') {
                    return (
                        entity.instrument.instrumentId === instrumentId ||
                        entity.instrument.underlyingInstrumentId === instrumentId
                    );
                } else {
                    return (
                        instrumentId.includes(entity.instrument.instrumentId) ||
                        instrumentId.includes(entity.instrument.underlyingInstrumentId)
                    );
                }
            }
        });

    /** accumulateTransactions$ - accumulate transactions by period
     * @param transactionIds - transaction ids to accumulate
     * @param period - period to accumulate by
     * @param accumulatorFunction - function used to accumulate transactions
     * @param returnAsObject - return as object instead of array
     * @returns Observable<Array<T>> - array of accumulated transactions
     */
    accumulateTransactions$ = <T>(
        transactionIds: Array<number>,
        period: 'yyyy' | 'MMM yy' | 'QQQ yy' = 'yyyy',
        accumulatorFunction: (transaction: Transaction, previous?: T) => T,
        returnAsObject = false
    ) => {
        // select all transactions based on transactionId...
        return this.selectMany([...transactionIds]).pipe(
            map((transactions) =>
                // ...and sort them ascending by transaction date
                transactions.sort(
                    (a, b) =>
                        new Date(a.transactionDateTime).getTime() -
                        new Date(b.transactionDateTime).getTime()
                )
            ),
            map((transactions) => {
                // return empty array if no transactions
                if (!transactions || transactions.length === 0) {
                    return [];
                }

                // create placeholder for groups/periods
                const GROUPS: { [period: string]: any } = {};

                // loop through all transactions...
                for (let i = 0; i < transactions.length; i++) {
                    // ...declare transaction
                    const TRANSACTION = transactions[i];
                    // ...declare group based on formatted dated (according to chosen period)
                    const GROUP = format(
                        new Date(TRANSACTION.transactionDateTime),
                        period
                    );
                    // ...group transactions by group
                    if (!GROUPS[GROUP]) {
                        // ...if group does not exist, create it
                        GROUPS[GROUP] = {
                            // ...pass transaction to callback function and use result as value for group
                            ...accumulatorFunction(TRANSACTION)
                        };
                    } else {
                        /* ...if group exists, pass transaction and previous value for group to
                         callback function and use result as new value for group */
                        GROUPS[GROUP] = {
                            ...accumulatorFunction(TRANSACTION, GROUPS[GROUP])
                        };
                    }
                }

                // create placeholder for group range containing all groups between first and last transaction
                let GROUP_RANGE: { [period: string]: any };
                const FIRST_TRANSACTION = new Date(transactions[0].transactionDateTime);
                const LAST_TRANSACTION = new Date(
                    transactions[transactions.length - 1].transactionDateTime
                );

                let timestampFunction: Function;

                // assign group range based on period
                if (period === 'QQQ yy') {
                    GROUP_RANGE = eachQuarterOfInterval({
                        start: FIRST_TRANSACTION,
                        end: LAST_TRANSACTION
                    });
                    timestampFunction = startOfQuarter;
                } else if (period === 'MMM yy') {
                    GROUP_RANGE = eachMonthOfInterval({
                        start: FIRST_TRANSACTION,
                        end: LAST_TRANSACTION
                    });
                    timestampFunction = startOfMonth;
                } else if (period === 'yyyy') {
                    GROUP_RANGE = eachYearOfInterval({
                        start: FIRST_TRANSACTION,
                        end: LAST_TRANSACTION
                    });
                    timestampFunction = startOfYear;
                }

                // loop through group range...
                const ALL_GROUPS: { [key: string]: T } = GROUP_RANGE.reduce(
                    (groups: { [key: string]: T }, date: number | Date) => {
                        // ...declare group name based on formatted dated (according to chosen period)
                        const group = format(date, period);

                        // ...get previous group name based on formatted dated (according to chosen period)
                        let previous;
                        if (period === 'QQQ yy') {
                            previous = format(subQuarters(date, 1), period);
                        } else if (period === 'MMM yy') {
                            previous = format(subMonths(date, 1), period);
                        } else if (period === 'yyyy') {
                            previous = format(subYears(date, 1), period);
                        }

                        // reassign groups for whole range
                        groups[group] = {
                            ...(GROUPS[group] ||
                                accumulatorFunction(null, groups[previous])),
                            period: group,
                            periodStart: timestampFunction(date).getTime()
                        };

                        return groups;
                    },
                    {}
                );
                return returnAsObject ? ALL_GROUPS : Object.values(ALL_GROUPS);
            })
        );
    };

    /**
     * Select transactions by group id
     * Fallback if no transactions are found, fetch from server
     * If no fallback is required, consider using selectTransactionByFilter$ instead
     * @param groupId - group id
     * @param dateFrom - date from
     * @param dateTo - date to
     * @returns Observable<Transaction[]>
     */
    selectTransactionsByGroup$(
        groupId: number,
        dateFrom?: Date,
        dateTo?: Date
    ): Observable<Transaction[]> {
        return this.selectTransactionByFilter$({
            transactionGroupId: {
                eq: Number(groupId)
            }
        }).pipe(
            take(1),
            switchMap((filteredTransactions) => {
                if (filteredTransactions.length === 0) {
                    return dateFrom && dateTo
                        ? this._service.getTransactionsBetween(
                              setFirstHourOfDayUTC(dateFrom),
                              setLastHourOfDayUTC(dateTo)
                          )
                        : this._service.get();
                }
                return of(filteredTransactions);
            })
        );
    }

    /** accumulateTransactionsByGroup - accumulate time series data by group
     * @param transactions - transactions to accumulate
     * @param accumulatorFunction - function used to accumulate transactions
     * @param groupBy - optionally group transactions by a key e.g. 'transactionDateTime' and an optional value modifier e.g. format(value, 'yyyy-MM')
     * @returns Observable<Array<R>> - array of accumulated transactions
     */
    accumulateTransactionsByGroup = <T, R>(
        transactions: Array<Transaction<T>>,
        accumulatorFunction: (transaction: Transaction<T>, previous?: R) => R,
        groupBy?: QueryFilterGroupBy
    ) => {
        if (groupBy) {
            // create placeholder for groups
            const GROUPS: { [group: string | number]: R } = {};
            for (let i = 0; i < transactions.length; i++) {
                const TRANSACTION = transactions[i];
                let group = get<string | number>(transactions[i], groupBy.key);
                // if groupBy.valueModifier is provided...
                if (groupBy.valueModifier) {
                    // ...use it to modify the group value
                    group = groupBy.valueModifier(group);
                }
                // add group
                GROUPS[group] = accumulatorFunction(TRANSACTION, GROUPS[group]);
            }
            return GROUPS;
        } else {
            let accumulated: R;
            for (let i = 0; i < transactions.length; i++) {
                const TRANSACTION = transactions[i];
                accumulated = accumulatorFunction(TRANSACTION, accumulated);
            }
            return accumulated;
        }
    };

    getInstrumentDataForTransactionGroup(transactionGroupId) {
        return this.getAll().find(
            (transaction) =>
                transaction.transactionGroupId === transactionGroupId &&
                transaction.instrument.type !== 'CASH'
        );
    }

    /** lastDataEntryByUser$
     * @description date when user last added or modified a time series value during active session
     * @returns Observable<Date>
     */
    get lastDataEntryByUser$() {
        return this.select((state) => state.ui.lastEntryByUser).pipe(
            distinctUntilChanged(),
            shareReplay(1)
        );
    }
}
